import { Box, Stack, Typography } from '@mui/material';
import { CircleSlash, MessageSquareOff, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactNode } from 'react';
import type { SiteOverviewSummary } from 'src/graphql/entities/address-summary';
import { ReasoningPopover } from './reasoning-popover';
import {
  briefActions,
  FACTOR_LABELS,
  humanize,
  resolveReferences,
  riskTone,
  sentimentTone,
  trendTone,
} from './site-overview.helpers';
import {
  badgeSx,
  briefActionSx,
  donutRootSx,
  donutScoreSx,
  donutSvgSx,
  driverRowSx,
  driverSignSx,
  factorFillSx,
  factorLabelSx,
  factorMetaRowSx,
  factorTrackSx,
  factorValueSx,
  healthScoreSx,
  moodCopySx,
  notEnoughCopySx,
  notEnoughIconSx,
  notEnoughNoteSx,
  panelHeadingSx,
  panelSx,
  summaryBodySx,
  type Tone,
} from './site-overview.styles';

export const Panel = ({ children, ...rest }: { children: ReactNode; gridColumn?: string }) => (
  <Box data-site-overview-panel sx={panelSx} {...rest}>
    {children}
  </Box>
);

const PanelHeading = ({ children, tight }: { children: string; tight?: boolean }) => (
  <Typography component="h3" sx={(theme) => panelHeadingSx(theme, tight)}>
    {children}
  </Typography>
);

const Badge = ({
  label,
  tone = 'neut',
  icon,
}: {
  label: string;
  tone?: Tone;
  icon?: ReactNode;
}) => (
  <Box component="span" sx={(theme) => badgeSx(theme, tone)}>
    {icon}
    {label}
  </Box>
);

export const Donut = ({
  score,
  tone,
  size = 72,
  scoreFontSize,
}: {
  score: number;
  tone: Tone;
  size?: number;
  scoreFontSize?: number;
}) => (
  <Box sx={{ ...donutRootSx, width: size, height: size }}>
    <Box
      component="svg"
      viewBox="0 0 36 36"
      width={size}
      height={size}
      sx={(theme) => donutSvgSx(theme, tone)}
    >
      <circle className="track" cx="18" cy="18" r="15.5" strokeWidth="3" fill="none" />
      <circle
        className="fill"
        cx="18"
        cy="18"
        r="15.5"
        strokeWidth="3"
        fill="none"
        pathLength={100}
        strokeDasharray={`${score} 100`}
        strokeLinecap="round"
        transform="rotate(-90 18 18)"
      />
    </Box>
    <Typography
      sx={(theme) => ({
        ...donutScoreSx(theme),
        fontSize: scoreFontSize ?? size * (22 / 72),
        lineHeight: 1,
      })}
    >
      {score}
    </Typography>
  </Box>
);

const FactorBar = ({ label, value }: { label: string; value: number }) => (
  <Box>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      sx={factorMetaRowSx}
    >
      <Typography sx={factorLabelSx}>{label}</Typography>
      <Typography sx={factorValueSx}>{value}</Typography>
    </Stack>
    <Box sx={factorTrackSx}>
      <Box sx={(theme) => factorFillSx(theme, value)} />
    </Box>
  </Box>
);

export const NotEnoughEvidence = ({
  title,
  icon,
  note,
}: {
  title: string;
  icon: ReactNode;
  note?: string;
}) => (
  <Panel>
    <PanelHeading tight={Boolean(note)}>{title}</PanelHeading>
    <Typography sx={(theme) => notEnoughCopySx(theme, note)}>Not enough evidence</Typography>
    {note ? (
      <Stack direction="row" gap="11px" alignItems="flex-start">
        <Box sx={notEnoughIconSx}>{icon}</Box>
        <Typography sx={notEnoughNoteSx}>{note}</Typography>
      </Stack>
    ) : null}
  </Panel>
);

function trendIcon(trend: string): ReactNode {
  if (trend === 'declining') {
    return <TrendingDown />;
  }
  if (trend === 'improving') {
    return <TrendingUp />;
  }
  return <Minus />;
}

export const SiteHealthPanel = ({
  overview,
  subjects,
  citationsLoading,
}: {
  overview: SiteOverviewSummary;
  subjects: Map<string, string>;
  citationsLoading: boolean;
}) => {
  const health = overview.siteHealth;

  if (health.assessmentStatus === 'not_assessed' || health.overallScore === null) {
    return (
      <NotEnoughEvidence
        title="Site Health"
        icon={<CircleSlash />}
        note={health.overallScoreReasoning || health.reasoning}
      />
    );
  }

  const score = health.overallScore;
  const tone = riskTone(health.riskLevel);
  const factors = health.factors;

  return (
    <Panel>
      <PanelHeading>Site Health</PanelHeading>
      <Stack direction="row" alignItems="center" gap="16px" sx={{ mb: '18px' }}>
        <Donut score={score} tone={tone} />
        <Stack direction="row" flexWrap="wrap" gap="7px">
          <Badge
            label={health.trend}
            tone={trendTone(health.trend)}
            icon={trendIcon(health.trend)}
          />
          {health.riskLevel ? <Badge label={health.riskLevel} tone={tone} /> : null}
        </Stack>
      </Stack>
      {factors ? (
        <Stack gap="12px">
          {FACTOR_LABELS.map(({ id, label }) => (
            <FactorBar key={id} label={label} value={factors[id]} />
          ))}
        </Stack>
      ) : null}
      <ReasoningPopover
        label="Overall health"
        value={
          <Box component="b" sx={(theme) => healthScoreSx(theme, tone)}>
            {score}
          </Box>
        }
        paragraphs={[
          health.overallScoreReasoning,
          health.reasoning,
          health.riskLevelReasoning,
        ]}
        references={resolveReferences(overview, health.conversationIds, subjects)}
        citationIds={health.conversationIds}
        citationsLoading={citationsLoading}
      />
    </Panel>
  );
};

export const SentimentPanel = ({
  overview,
  subjects,
  citationsLoading,
}: {
  overview: SiteOverviewSummary;
  subjects: Map<string, string>;
  citationsLoading: boolean;
}) => {
  const sentiment = overview.clientSentiment;

  if (sentiment.assessmentStatus === 'unknown') {
    return (
      <NotEnoughEvidence
        title="Current Site Sentiment"
        icon={<MessageSquareOff />}
        note={sentiment.overallReasoning}
      />
    );
  }

  const drivers = [
    ...(sentiment.sentimentDrivers.positive ?? []).map((d) => ({ sign: '+', text: d.driver })),
    ...(sentiment.sentimentDrivers.negative ?? []).map((d) => ({ sign: '−', text: d.driver })),
  ];

  return (
    <Panel>
      <PanelHeading>Current Site Sentiment</PanelHeading>
      <Stack direction="row" alignItems="center" gap="8px" flexWrap="wrap" sx={{ mb: '13px' }}>
        <Badge label={humanize(sentiment.overall)} tone={sentimentTone(sentiment.overall)} />
        {sentiment.lastKnownMood ? (
          <Typography sx={moodCopySx}>{sentiment.lastKnownMood}</Typography>
        ) : null}
      </Stack>
      <Stack gap="8px">
        {drivers.map((driver) => (
          <Box key={driver.text} sx={driverRowSx}>
            <Box component="span" sx={(theme) => driverSignSx(theme, driver.sign === '+')}>
              {driver.sign}
            </Box>
            <span>{driver.text}</span>
          </Box>
        ))}
      </Stack>
      <ReasoningPopover
        label="Site sentiment"
        value={
          <Badge label={humanize(sentiment.overall)} tone={sentimentTone(sentiment.overall)} />
        }
        paragraphs={[sentiment.overallReasoning]}
        references={resolveReferences(overview, sentiment.conversationIds, subjects)}
        citationIds={sentiment.conversationIds}
        citationsLoading={citationsLoading}
      />
    </Panel>
  );
};

export const SupportBriefPanel = ({ overview }: { overview: SiteOverviewSummary }) => {
  const actions = briefActions(overview);
  if (actions.length === 0) {
    return null;
  }

  return (
    <Panel>
      <PanelHeading>Support Brief</PanelHeading>
      <Stack gap="9px" sx={{ mt: '2px' }}>
        {actions.map((action) => (
          <Typography key={action} sx={briefActionSx}>
            {action}
          </Typography>
        ))}
      </Stack>
    </Panel>
  );
};

export const ShortSummaryPanel = ({ shortSummary }: { shortSummary: string }) => (
  <Panel>
    <PanelHeading>Site Summary</PanelHeading>
    <Typography sx={summaryBodySx}>{shortSummary}</Typography>
  </Panel>
);
